import { useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import { createMultiplayerSessionWithQuestions, getFormatDistribution } from '../../../shared/services/multiplayerQuestionService';
import { normalizeQuestionLocale } from '../../../shared/utils/questionLocales';
import type { GameRoom, Player } from '../../../types/multiplayer';
import { useLanguageStore } from '../../quiz-core/languageStore';

type ActiveSessionRow = {
  id: string;
  started_at: string;
  status: string | null;
};

interface UseRoomGameStartOptions {
  isHost: boolean;
  userId: string | null;
  roomId: string;
  currentRoom: GameRoom | null;
  players: Player[];
  setCurrentRoom: (room: GameRoom) => void;
}

export function useRoomGameStart({
  isHost,
  userId,
  roomId,
  currentRoom,
  players,
  setCurrentRoom,
}: UseRoomGameStartOptions) {
  const appLanguage = useLanguageStore((state) => state.language);

  const startGame = useCallback(async (roomId: string, categoryId: number, difficultyId: number) => {
    try {
      console.log('[useRoomGameStart] Attempting to start game:', { roomId, categoryId, difficultyId });
      const currentPlayerIsInRoom = Boolean(userId && players.some((player) => player.id === userId));
      const canCurrentPlayerStart = isHost || currentPlayerIsInRoom;

      if (!canCurrentPlayerStart || !roomId || !currentRoom) {
        throw new Error('Cannot start game: Missing required data');
      }
      // 1. First, check if there are players
      if (players.length === 0) {
        throw new Error('No players in the room');
      }
      // 2. Refresh players to get the latest ready status
      const { data: updatedPlayers, error: playersError } = await supabase
        .from('multiplayer_game_room_players')
        .select('*')
        .eq('room_id', roomId);
      if (playersError) throw playersError;
      // 3. Check if all players are ready
      const readyPlayers = (updatedPlayers ?? []) as Player[];
      const allPlayersReady = readyPlayers.length > 0 &&
        readyPlayers.every((player) => player.is_ready);
      if (!allPlayersReady) {
        const notReadyPlayers = readyPlayers
          .filter((player) => !player.is_ready)
          .map((player) => player.name || player.id || 'Unknown player');
        console.log('[useRoomGameStart] Not all players ready:', notReadyPlayers);
        throw new Error(`Waiting for ${notReadyPlayers.join(', ')} to be ready`);
      }
      console.log('[useRoomGameStart] All players ready. Proceeding to start game.');
      // 4. Verify the room exists and is in waiting status
      const { data: roomData, error: roomError } = await supabase
        .from('multiplayer_game_rooms')
        .select('status')
        .eq('id', roomId)
        .single();
      if (roomError || !roomData) {
        throw new Error('Failed to verify room status');
      }
      if (roomData.status !== 'waiting') {
        setCurrentRoom({
          ...currentRoom,
          status: roomData.status as GameRoom['status'],
        });
        console.log('[useRoomGameStart] Room is not in waiting status:', roomData.status);
        throw new Error(`Cannot start game: Room is ${roomData.status}`);
      }
      // 5. Close any stale "active" sessions before creating a new one.
      // If the room is back in `waiting`, resuming an older `ended_at = null`
      // session can reuse an old `started_at`, which makes the backend think
      // the new round has already timed out.
      const { data: existingSessions, error: findSessionError } = await supabase
        .from('multiplayer_game_sessions')
        .select('id, status, started_at')
        .eq('room_id', roomId)
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(5);
      if (findSessionError) {
        throw findSessionError;
      }

      const staleSessions = (existingSessions ?? []) as ActiveSessionRow[];
      if (staleSessions.length > 0) {
        const staleSessionIds = staleSessions.map((session) => session.id);
        const staleSessionClosedAt = new Date().toISOString();

        console.warn('[useRoomGameStart] Closing stale active sessions before new start:', {
          roomId,
          staleSessionIds,
          sessions: staleSessions,
        });

        const { error: closeSessionsError } = await supabase
          .from('multiplayer_game_sessions')
          .update({
            status: 'completed',
            ended_at: staleSessionClosedAt,
          })
          .in('id', staleSessionIds);

        if (closeSessionsError) {
          throw new Error(`Failed to close stale sessions: ${closeSessionsError.message}`);
        }
      }
      // 6. Use enhanced question generation to start a new game
      console.log('[useRoomGameStart] Generating enhanced questions for multiplayer...');
      
      // Get format distribution based on difficulty
      const formatDistribution = getFormatDistribution(difficultyId, false);
      
      // Create session with enhanced questions using our new service
      const sessionId = await createMultiplayerSessionWithQuestions(roomId, currentRoom.host_id, {
        categoryId: categoryId || 5, // Default to Miracles (has all question formats) if not specified
        difficultyId: difficultyId || 1, // Default to easy if not specified  
        totalQuestions: 10,
        languageCode: normalizeQuestionLocale(appLanguage),
        formatDistribution
      });
      
      if (!sessionId) {
        throw new Error('Failed to create enhanced multiplayer session');
      }
      
      // Fetch the created session to get the full data
      const { data: sessionData, error: sessionError } = await supabase
        .from('multiplayer_game_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();
        
      if (sessionError || !sessionData) {
        throw new Error(sessionError?.message || 'Failed to fetch created session');
      }
      
      // Update room status manually since we're not using the old RPC
      const { error: roomUpdateError } = await supabase
        .from('multiplayer_game_rooms') 
        .update({
          status: 'in_progress',
          current_session_id: sessionId,
          updated_at: new Date().toISOString()
        })
        .eq('id', roomId);
        
      if (roomUpdateError) {
        throw new Error(`Failed to update room status: ${roomUpdateError.message}`);
      }
      
      // Create result in the same format as the old RPC for compatibility
      const result = {
        success: true,
        session: sessionData,
        room: {
          ...currentRoom,
          status: 'in_progress',
          current_session_id: sessionId,
          updated_at: new Date().toISOString()
        }
      };
      
      console.log('[useRoomGameStart] Enhanced session created successfully:', {
        sessionId,
        questionCount: sessionData.questions?.length || 0,
        formats: sessionData.questions ? 
          sessionData.questions.reduce((acc: Record<string, number>, q: { question_type?: string }) => {
            const questionType = q.question_type || 'unknown';
            acc[questionType] = (acc[questionType] || 0) + 1;
            return acc;
          }, {}) : {}
      });
      // 7. Update local state with the new session
      const finalUpdatedRoom: GameRoom = {
        ...(currentRoom || {} as Partial<GameRoom>),
        ...(result.room || {} as Partial<GameRoom>),
        id: (result.room?.id || currentRoom?.id || roomId) as string,
        status: 'in_progress',
        current_session_id: result.session.id,
        questions: result.session.questions,
        current_question_index: 0,
        updated_at: new Date().toISOString(),
        name: (result.room?.name ?? currentRoom?.name ?? '') as string,
        category_id: (result.room?.category_id ?? currentRoom?.category_id ?? undefined) as (string | string[] | undefined),
        difficulty: (result.room?.difficulty ?? currentRoom?.difficulty ?? 'medium') as 'easy' | 'medium' | 'hard',
        max_players: (result.room?.max_players ?? currentRoom?.max_players ?? 2) as number,
        created_at: (result.room?.created_at ?? currentRoom?.created_at ?? new Date().toISOString()) as string,
        host_id: (result.room?.host_id ?? currentRoom?.host_id ?? '') as string,
      };
      setCurrentRoom(finalUpdatedRoom);
      console.log('[useRoomGameStart] Game started. Final updated room:', finalUpdatedRoom, 'Session:', result.session);
      return result.session;
    } catch (error) {
      // Reset room status if something goes wrong
      try {
        const resetRoom: GameRoom = {
          ...currentRoom,
          id: (currentRoom?.id || roomId) as string,
          name: currentRoom?.name ?? '',
          code: currentRoom?.code ?? '',
          host_id: currentRoom?.host_id ?? '',
          max_players: currentRoom?.max_players ?? 2,
          is_private: currentRoom?.is_private ?? false,
          status: 'waiting',
          created_at: currentRoom?.created_at ?? new Date().toISOString(),
          updated_at: new Date().toISOString(),
          category_id: currentRoom?.category_id ?? undefined,
          difficulty_id: currentRoom?.difficulty_id ?? undefined,
          difficulty: currentRoom?.difficulty ?? undefined,
          time_per_question: currentRoom?.time_per_question ?? undefined,
          question_count: currentRoom?.question_count ?? undefined,
          current_session_id: currentRoom?.current_session_id ?? null,
          current_question_index: currentRoom?.current_question_index ?? 0,
          player_count: currentRoom?.player_count ?? undefined,
          questions: currentRoom?.questions ?? undefined,
        };
        setCurrentRoom(resetRoom);
        // Update the database
        const { error: resetError } = await supabase
          .from('multiplayer_game_rooms')
          .update({
            status: 'waiting',
            updated_at: new Date().toISOString(),
          })
          .eq('id', roomId);
        if (resetError) {
          console.error('[useRoomGameStart] Error resetting room status:', resetError);
        }
      } catch (resetError) {
        console.error('[useRoomGameStart] Error in resetRoom catch:', resetError);
      }
      console.error('[useRoomGameStart] Error in startGame:', error);
      throw error;
    }
  }, [appLanguage, currentRoom, isHost, players, roomId, setCurrentRoom, userId]);

  return { startGame };
}
